import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDate,
  normalizePolicyReportProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { useLocation } from "react-router-dom";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import ReportedPoliciesDataGrid from "../../components/DataTables/ReportedPoliciesDataGrid.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";

const ReportedInsurances: React.FC = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    company: "",
    policyType: "",
    agent: "",
    selectedOffices: [],
    reportNumber: "",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [officesOptions, setOfficesOptions] = useState<Option[]>([]);
  const [selectedOfficeValues, setSelectedOfficeValues] = useState<string[]>(
    []
  );
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(
    new Set()
  );
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setFormState({
      fromDate: "",
      company: "",
      policyType: "",
      agent: "",
      selectedOffices: [],
      reportNumber: "",
    });
    setPolicies([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, officesRes, policyTypesRes] =
          await Promise.all([
            InsuranceService.getAllAgents(jwt),
            InsuranceService.getAllInsuranceCompanies(jwt),
            InsuranceService.getAllOffices(jwt),
            InsuranceService.getAllPolicyTypes(jwt),
          ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setOffices(officesRes as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    setOfficesOptions(
      Object.entries(offices).map(([k, v]) => ({
        text: v,
        value: k,
        id: k,
        selected: selectedOfficeValues.includes(k),
      }))
    );
  }, [offices, selectedOfficeValues]);

  const handleOfficeSelectionChange = (selectedValues: string[]) => {
    setSelectedOfficeValues(selectedValues);
    setFormState((prev) => ({ ...prev, selectedOffices: selectedValues }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.fromDate) {
      setAlert("Моля, въведете стартова дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchReportedInsurances(
        { ...formState, fromDate: revertNormalizeDate(formState.fromDate) },
        jwt
      );
      const normalizedPolicies = Object.values(result).map((policy) => {
        const normalizedPolicy = normalizePolicyReportProperties(policy);
        return {
          ...normalizedPolicy,
          policyAmount: normalizedPolicy.policyAmount.toFixed(2),
          startDate: normalizeDate(normalizedPolicy.startDate),
          endDate: normalizeDate(normalizedPolicy.endDate),
          policyDate: normalizeDate(normalizedPolicy.policyDate),
          installmentDate: normalizedPolicy.installmentDate
            ? normalizeDate(normalizedPolicy.installmentDate.trim())
            : "",
        };
      });
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  return (
    <>
      <Breadcrumb pageName="Отчетени полици в ЗК" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Отчетени полици в ЗК" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <label className={labelClass}>Номер на отчет</label>
              <input
                type="text"
                name="reportNumber"
                value={formState.reportNumber}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    reportNumber: e.target.value,
                  }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <MultiSelect
                options={officesOptions}
                setOptions={setOfficesOptions}
                placeholder="Изберете офис"
                onSelectionChange={handleOfficeSelectionChange}
                id="multi-select-office"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <button
              type="submit"
              className="bg-primary w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>

        <ReportedPoliciesDataGrid
          policies={policies}
          isLoading={isLoading}
          selectedPolicies={selectedPolicies}
          setSelectedPolicies={setSelectedPolicies}
        />
      </div>
    </>
  );
};

export default ReportedInsurances;
